import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { apiFetch } from "@/services/http/api-fetch";

type Resource = { id: string; fileName: string; sizeBytes: number | null };

/**
 * H2: the builder's Resources step — PDFs for the whole course (shown to enrolled students under
 * "Course resources" in every session). Add, replace (same entry, new file; the old file is
 * deleted) and remove.
 */
export function CourseResourcesEditor({ courseId }: { courseId: string }) {
  const t = useTranslations("instructor");
  const tRoot = useTranslations();
  const errorText = (err: unknown, fallbackKey: string) =>
    apiErrorText(err, tRoot, `instructor.${fallbackKey}`, { namespace: "instructor.errors" });
  const [resources, setResources] = useState<Resource[]>([]);
  const [loaded, setLoaded] = useState(false);
  // W-26 sibling: a failed list load is not "no resources".
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const replaceInput = useRef<HTMLInputElement>(null);
  const [replacing, setReplacing] = useState<Resource | null>(null);
  const base = `/v1/instructor/courses/${courseId}/resources`;

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const res = await apiFetch(base);
        if (!res.ok) throw await readApiErrorResponse(res);
        const json = (await res.json()) as { data: { resources: Resource[] } };
        if (active) setResources(json.data.resources);
      } catch (err) {
        if (active) {
          setLoadFailed(true);
          setError(errorText(err, "loadResourcesFailed"));
        }
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
    // errorText is recreated each render; the list depends on the course only.
  }, [base]);

  async function send(path: string, file: File, fallbackKey: string) {
    const form = new FormData();
    form.append("file", file);
    const res = await apiFetch(path, { method: "POST", body: form });
    if (!res.ok) {
      setError(errorText(await readApiErrorResponse(res), fallbackKey));
      return null;
    }
    return ((await res.json()) as { data: { resource: Resource } }).data.resource;
  }

  async function handleAdd(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const resource = await send(base, file, "uploadResourceFailed");
      if (resource) setResources((prev) => [...prev, resource]);
    } catch (err) {
      setError(errorText(err, "uploadResourceFailed"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  }

  function startReplace(resource: Resource) {
    setReplacing(resource);
    replaceInput.current?.click();
  }

  async function handleReplace(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const target = replacing;
    e.target.value = "";
    if (!file || !target) return;
    setBusy(true);
    setError(null);
    try {
      const resource = await send(`${base}/${target.id}/replace`, file, "replacePdfFailed");
      if (resource) {
        setResources((prev) => prev.map((row) => (row.id === target.id ? resource : row)));
      }
    } catch (err) {
      setError(errorText(err, "replacePdfFailed"));
    } finally {
      setBusy(false);
      setReplacing(null);
    }
  }

  async function handleDelete(resource: Resource) {
    if (!window.confirm(t("deletePdfConfirm", { name: resource.fileName }))) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${base}/${resource.id}`, { method: "DELETE" });
      if (!res.ok) {
        setError(errorText(await readApiErrorResponse(res), "deleteResourceFailed"));
        return;
      }
      setResources((prev) => prev.filter((row) => row.id !== resource.id));
    } catch (err) {
      setError(errorText(err, "deleteResourceFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="builder-panel-resources" className="space-y-3">
      <h2 className="text-lg font-medium">{t("resourcesTitle")}</h2>
      <p className="text-sm text-[var(--text-muted)]">{t("resourcesHint")}</p>
      <label className="block text-sm">
        <span className="font-medium">{t("uploadResourceLabel")}</span>
        <input
          type="file"
          accept="application/pdf,.pdf"
          data-testid="upload-course-resource"
          disabled={busy}
          onChange={handleAdd}
          className="mt-2 block text-sm"
        />
      </label>
      <input
        ref={replaceInput}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        data-testid="replace-course-resource-input"
        aria-hidden
        tabIndex={-1}
        onChange={handleReplace}
      />
      {resources.length > 0 ? (
        <ul className="space-y-1 text-sm" data-testid="course-resource-list">
          {resources.map((file) => (
            <li
              key={file.id}
              className="flex flex-wrap items-center gap-3"
              data-testid={`course-resource-row-${file.id}`}
            >
              <span>{file.fileName}</span>
              <button
                type="button"
                className="text-sm underline disabled:opacity-60"
                disabled={busy}
                aria-label={t("replacePdfLabel", { name: file.fileName })}
                data-testid={`replace-course-resource-${file.id}`}
                onClick={() => startReplace(file)}
              >
                {t("replacePdf")}
              </button>
              <button
                type="button"
                className="text-sm underline disabled:opacity-60"
                disabled={busy}
                aria-label={t("deletePdfLabel", { name: file.fileName })}
                data-testid={`delete-course-resource-${file.id}`}
                onClick={() => void handleDelete(file)}
              >
                {t("deletePdf")}
              </button>
            </li>
          ))}
        </ul>
      ) : loaded && !loadFailed ? (
        <p className="text-sm text-[var(--text-muted)]">{t("noResources")}</p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-red-600" data-testid="course-resources-error">
          {error}
        </p>
      ) : null}
    </section>
  );
}
